import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import { permissionMiddleware } from "~/lib/permission-middleware";
import * as summitConstants from "~/lib/summit";
import type * as SummitTypes from "~/lib/summit";
import { normalizeSummitDate } from "~/lib/summit";

function buildVipUpsellUrl({
  baseUrl,
  fullName,
  email,
}: {
  baseUrl: string;
  fullName: string | null;
  email: string | null;
}): string {
  const searchParams = new URLSearchParams();

  if (fullName) {
    searchParams.set("full_name", fullName);
  }

  if (email) {
    searchParams.set("email", email);
  }

  if (searchParams.size === 0) {
    return baseUrl;
  }

  return `${baseUrl}?${searchParams.toString()}`;
}

// Get active summit with all data
export const getActiveSummitData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const summitServer = await import("~/lib/server/summit");
    const activeSummit = await summitServer.getActiveSummit();
    if (!activeSummit) {
      throw new Error("No active summit found");
    }

    const summitData = await summitServer.getSummitWithDays(activeSummit.id);
    if (!summitData) {
      throw new Error("Summit data not found");
    }

    const userRegistration = await summitServer.getUserSummitRegistration(
      context.user.id,
      activeSummit.id,
    );

    const fullName =
      [context.user.firstName, context.user.lastName]
        .filter((part): part is string => Boolean(part && part.trim()))
        .join(" ") || null;
    const email = context.user.email ?? null;
    const vipUpsellUrl = buildVipUpsellUrl({
      baseUrl: summitConstants.SUMMIT_VIP_UPSELL_URL,
      fullName,
      email,
    });

    // Get all session IDs
    const sessionIds = summitData.days.flatMap((day) =>
      day.sessions.map((session) => session.id),
    );

    const sessionRegistrations = await summitServer.getUserSessionRegistrations(
      context.user.id,
      sessionIds,
    );

    const registeredSessionIds = new Set(
      Array.from(sessionRegistrations.entries())
        .filter(([, isRegistered]) => isRegistered)
        .map(([sessionId]) => sessionId),
    );

    const workshopRegistrationsCount = summitData.days.reduce((count, day) => {
      return (
        count +
        day.sessions.filter(
          (session) =>
            session.sessionFormat === "workshop" &&
            registeredSessionIds.has(session.id),
        ).length
      );
    }, 0);

    // Transform to frontend format
    const days = summitServer.transformSummitData(
      summitData.summit,
      summitData.days,
    );

    // Create footer variants for each session
    const footerVariants = new Map<string, SummitTypes.SessionFooterVariant>();
    const now = new Date();

    summitData.days.forEach((day) => {
      day.sessions.forEach((session) => {
        const sessionEndTime = new Date(
          session.startTimeUtc.getTime() + session.durationMinutes * 60 * 1000,
        );
        const isSessionOver = now >= sessionEndTime;

        if (session.recordingUrl && isSessionOver) {
          footerVariants.set(session.id, {
            type: "viewing-recording",
            recordingUrl: session.recordingUrl,
          });
        } else {
          const isRegistered = sessionRegistrations.get(session.id) ?? false;
          const isWorkshop = session.sessionFormat === "workshop";

          const limitReached =
            isWorkshop &&
            !isRegistered &&
            workshopRegistrationsCount >=
              summitConstants.SUMMIT_WORKSHOP_REGISTRATION_LIMIT;

          footerVariants.set(session.id, {
            type: "registration",
            isRegistered,
            registrationUrl: `/summit/sessions/${session.id}/register`,
            liveUrl: isRegistered ? (session.liveUrl ?? undefined) : undefined,
            limitReached,
            limit: isWorkshop
              ? summitConstants.SUMMIT_WORKSHOP_REGISTRATION_LIMIT
              : undefined,
            registeredCount: isWorkshop
              ? workshopRegistrationsCount
              : undefined,
          });
        }
      });
    });

    return {
      summit: {
        ...summitData.summit,
        // Normalize date-only fields to UTC midnight to prevent timezone shifts during serialization
        startDate: normalizeSummitDate(summitData.summit.startDate),
        endDate: normalizeSummitDate(summitData.summit.endDate),
      },
      days,
      userRegistration: {
        ...userRegistration,
        workshopRegistrationsCount,
        workshopRegistrationLimit:
          summitConstants.SUMMIT_WORKSHOP_REGISTRATION_LIMIT,
      },
      footerVariants: Object.fromEntries(footerVariants),
      vipUpsellUrl,
    };
  });

export const getActiveSummitDataOptions = queryOptions({
  queryKey: ["summit", "active"],
  queryFn: () => getActiveSummitData(),
  staleTime: 5 * 60 * 1000, // 5 minutes
});

// Get summit data by specific ID (for multi-summit support)
export const getSummitDataById = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ summitId: z.string().uuid() }))
  .handler(async ({ data, context }) => {
    const summitServer = await import("~/lib/server/summit");

    const summitData = await summitServer.getSummitWithDays(data.summitId);
    if (!summitData) {
      throw new Error("Summit not found");
    }

    const userRegistration = await summitServer.getUserSummitRegistration(
      context.user.id,
      data.summitId,
    );

    const fullName =
      [context.user.firstName, context.user.lastName]
        .filter((part): part is string => Boolean(part && part.trim()))
        .join(" ") || null;
    const email = context.user.email ?? null;
    const vipUpsellUrl = buildVipUpsellUrl({
      baseUrl: summitConstants.SUMMIT_VIP_UPSELL_URL,
      fullName,
      email,
    });

    // Get all session IDs
    const sessionIds = summitData.days.flatMap((day) =>
      day.sessions.map((session) => session.id),
    );

    const sessionRegistrations = await summitServer.getUserSessionRegistrations(
      context.user.id,
      sessionIds,
    );

    const registeredSessionIds = new Set(
      Array.from(sessionRegistrations.entries())
        .filter(([, isRegistered]) => isRegistered)
        .map(([sessionId]) => sessionId),
    );

    const workshopRegistrationsCount = summitData.days.reduce((count, day) => {
      return (
        count +
        day.sessions.filter(
          (session) =>
            session.sessionFormat === "workshop" &&
            registeredSessionIds.has(session.id),
        ).length
      );
    }, 0);

    // Transform to frontend format
    const days = summitServer.transformSummitData(
      summitData.summit,
      summitData.days,
    );

    // Create footer variants for each session
    const footerVariants = new Map<string, SummitTypes.SessionFooterVariant>();
    const now = new Date();

    summitData.days.forEach((day) => {
      day.sessions.forEach((session) => {
        const sessionEndTime = new Date(
          session.startTimeUtc.getTime() + session.durationMinutes * 60 * 1000,
        );
        const isSessionOver = now >= sessionEndTime;

        if (session.recordingUrl && isSessionOver) {
          footerVariants.set(session.id, {
            type: "viewing-recording",
            recordingUrl: session.recordingUrl,
          });
        } else {
          const isRegistered = sessionRegistrations.get(session.id) ?? false;
          const isWorkshop = session.sessionFormat === "workshop";

          const limitReached =
            isWorkshop &&
            !isRegistered &&
            workshopRegistrationsCount >=
              summitConstants.SUMMIT_WORKSHOP_REGISTRATION_LIMIT;

          footerVariants.set(session.id, {
            type: "registration",
            isRegistered,
            registrationUrl: `/summit/sessions/${session.id}/register`,
            liveUrl: isRegistered ? (session.liveUrl ?? undefined) : undefined,
            limitReached,
            limit: isWorkshop
              ? summitConstants.SUMMIT_WORKSHOP_REGISTRATION_LIMIT
              : undefined,
            registeredCount: isWorkshop
              ? workshopRegistrationsCount
              : undefined,
          });
        }
      });
    });

    return {
      summit: {
        ...summitData.summit,
        // Normalize date-only fields to UTC midnight to prevent timezone shifts during serialization
        startDate: normalizeSummitDate(summitData.summit.startDate),
        endDate: normalizeSummitDate(summitData.summit.endDate),
      },
      days,
      userRegistration: {
        ...userRegistration,
        workshopRegistrationsCount,
        workshopRegistrationLimit:
          summitConstants.SUMMIT_WORKSHOP_REGISTRATION_LIMIT,
      },
      footerVariants: Object.fromEntries(footerVariants),
      vipUpsellUrl,
    };
  });

export const getSummitDataByIdOptions = (summitId: string) =>
  queryOptions({
    queryKey: ["summit", summitId],
    queryFn: () => getSummitDataById({ data: { summitId } }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

// Get user's summit registrations list (for settings page)
export const getUserSummitRegistrations = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const summitServer = await import("~/lib/server/summit");
    return summitServer.getUserSummitRegistrationsList(context.user.id);
  });

export const getUserSummitRegistrationsOptions = queryOptions({
  queryKey: ["summit", "user-registrations"],
  queryFn: () => getUserSummitRegistrations(),
  staleTime: 5 * 60 * 1000, // 5 minutes
});

// Get session data for public session page
export const getSessionData = createServerFn({ method: "GET" })
  .validator(
    z.object({
      sessionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }) => {
    const { buildSessionPageData } =
      await import("~/lib/server/summit-session-page");
    return buildSessionPageData(data.sessionId);
  });

export const getSessionDataOptions = (sessionId: string) =>
  queryOptions({
    queryKey: ["session", sessionId],
    queryFn: () => getSessionData({ data: { sessionId } }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

export const getSessionRegistrationState = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      sessionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data, context }) => {
    const summitServer = await import("~/lib/server/summit");
    const sessionData = await summitServer.getSessionWithSummit(data.sessionId);
    if (!sessionData) {
      throw new Error("Session not found");
    }

    const [
      summitRegistration,
      sessionRegistrations,
      workshopRegistrationsCount,
    ] = await Promise.all([
      summitServer.getUserSummitRegistration(
        context.user.id,
        sessionData.summit.id,
      ),
      summitServer.getUserSessionRegistrations(context.user.id, [
        sessionData.session.id,
      ]),
      summitServer.getUserWorkshopRegistrationsCount(
        context.user.id,
        sessionData.summit.id,
      ),
    ]);

    const isWorkshop = sessionData.session.sessionFormat === "workshop";
    const isSessionRegistered =
      sessionRegistrations.get(sessionData.session.id) ?? false;
    const limitReached =
      isWorkshop &&
      !isSessionRegistered &&
      workshopRegistrationsCount >=
        summitConstants.SUMMIT_WORKSHOP_REGISTRATION_LIMIT;

    return {
      summit: {
        id: sessionData.summit.id,
        title: sessionData.summit.title,
      },
      session: {
        id: sessionData.session.id,
        title: sessionData.session.title,
        sessionFormat: sessionData.session.sessionFormat,
        meetingUrl: sessionData.session.liveUrl ?? null,
      },
      registration: {
        summit: {
          isRegistered: summitRegistration.isRegistered,
          isVip: summitRegistration.isVip,
        },
        session: {
          isRegistered: isSessionRegistered,
        },
        isWorkshop,
        limitReached,
        workshopRegistrationsCount,
        workshopRegistrationLimit:
          summitConstants.SUMMIT_WORKSHOP_REGISTRATION_LIMIT,
      },
    };
  });

export const getSessionRegistrationStateOptions = (sessionId: string) =>
  queryOptions({
    queryKey: ["session", sessionId, "registration-state"],
    queryFn: () => getSessionRegistrationState({ data: { sessionId } }),
    staleTime: 30 * 1000,
  });

// Register for a session
export const registerForSession = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      sessionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data, context }) => {
    const summitServer = await import("~/lib/server/summit");
    await summitServer.registerForSession(context.user.id, data.sessionId);
    return { success: true };
  });

// Unregister from a session
export const unregisterFromSession = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    z.object({
      sessionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data, context }) => {
    const summitServer = await import("~/lib/server/summit");
    await summitServer.unregisterFromSession(context.user.id, data.sessionId);
    return { success: true };
  });

/**
 * Get the start time of the first session in the active summit.
 * Lightweight endpoint for countdown displays - minimal data transfer.
 */
export const getSummitCountdownTarget = createServerFn({
  method: "GET",
}).handler(async () => {
  const summitServer = await import("~/lib/server/summit");
  const startTime = await summitServer.getActiveSummitFirstSessionStart();
  return { startTimeUtc: startTime?.toISOString() ?? null };
});

export const getSummitCountdownTargetOptions = queryOptions({
  queryKey: ["summit", "countdown-target"],
  queryFn: () => getSummitCountdownTarget(),
  staleTime: 5 * 60 * 1000, // 5 minutes
});

// Public summit data fetcher (no auth required)
export const getPublicSummitData = createServerFn({ method: "GET" }).handler(
  async () => {
    const summitServer = await import("~/lib/server/summit");

    const activeSummit = await summitServer.getActiveSummit();
    if (!activeSummit) {
      return null;
    }

    const summitData = await summitServer.getSummitWithDays(activeSummit.id);
    if (!summitData) {
      return null;
    }

    // Transform to include display fields
    const transformedDays = summitServer.transformSummitData(
      summitData.summit,
      summitData.days,
    );

    return {
      summit: {
        id: summitData.summit.id,
        title: summitData.summit.title,
        // Normalize date-only fields to UTC midnight to prevent timezone shifts during serialization
        startDate: normalizeSummitDate(summitData.summit.startDate),
        endDate: normalizeSummitDate(summitData.summit.endDate),
      },
      days: transformedDays,
    };
  },
);

export const getPublicSummitDataOptions = queryOptions({
  queryKey: ["summit", "public"],
  queryFn: () => getPublicSummitData(),
  staleTime: 5 * 60 * 1000, // 5 minutes
});

/**
 * Lightweight endpoint for CTA data polling.
 * Returns only the CTA-related fields for the active summit.
 * Used by the agenda page to check for CTA visibility updates.
 *
 * Aliases: summit cta, call to action, live button
 */
export const getSummitCtaData = createServerFn({ method: "GET" }).handler(
  async () => {
    const summitServer = await import("~/lib/server/summit");
    const activeSummit = await summitServer.getActiveSummit();

    if (!activeSummit) {
      return null;
    }

    return {
      summitId: activeSummit.id,
      ctaText: activeSummit.ctaText,
      ctaUrl: activeSummit.ctaUrl,
      ctaAppearsOn: activeSummit.ctaAppearsOn,
    };
  },
);

export const getSummitCtaDataOptions = queryOptions({
  queryKey: ["summit", "cta"],
  queryFn: () => getSummitCtaData(),
  staleTime: 10 * 1000, // 10 seconds - short for live polling
  refetchInterval: 30 * 1000, // Poll every 30 seconds
});

/**
 * Update summit CTA fields directly.
 * Used by the admin CTA control page.
 */
export const updateSummitCta = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("summit:write")])
  .validator(
    z.object({
      summitId: z.string().uuid(),
      ctaText: z.string().nullable().optional(),
      ctaUrl: z.string().nullable().optional(),
      ctaAppearsOn: z.coerce.date().nullable().optional(),
    }),
  )
  .handler(async ({ data }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { summits } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    const updates: Partial<{
      ctaText: string | null;
      ctaUrl: string | null;
      ctaAppearsOn: Date | null;
    }> = {};

    if (data.ctaText !== undefined) updates.ctaText = data.ctaText;
    if (data.ctaUrl !== undefined) updates.ctaUrl = data.ctaUrl;
    if (data.ctaAppearsOn !== undefined)
      updates.ctaAppearsOn = data.ctaAppearsOn;

    const [updated] = await db
      .update(summits)
      .set(updates)
      .where(eq(summits.id, data.summitId))
      .returning({
        id: summits.id,
        ctaText: summits.ctaText,
        ctaUrl: summits.ctaUrl,
        ctaAppearsOn: summits.ctaAppearsOn,
      });

    return updated;
  });
